import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import api from "@/lib/api";
import { Copy, Gift, Users, Check, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/referrals/me");
      setData(res.data);
    } catch (err) {
      console.error("referrals load failed", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const link = data?.code ? `${window.location.origin}/r/${data.code}` : "";

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (clipErr) {
      console.debug("clipboard copy failed", clipErr);
      toast.error("Copy failed");
    }
  };

  const shareNative = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Career GPS AI",
          text: "I'm using Career GPS AI to plan my dream career — get 7 days of Pro free with my link:",
          url: link,
        });
      } catch (shareErr) {
        // AbortError = user cancelled the share sheet; everything else worth a debug log.
        if (shareErr?.name !== "AbortError") {
          console.debug("share failed", shareErr);
        }
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="referrals-page">
      <Navbar />
      <DashSidebar />
      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-5xl mx-auto lg:mx-0">
        <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// Growth · Win-Win</div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium">Invite a friend, both get a free 7 Days of Pro.</h1>
        <p className="text-zinc-500 mt-3 max-w-2xl text-sm sm:text-base">For every friend who signs up with your link, you each get 7 days of Pro added to your account — on top of any existing time.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 sm:mt-10">
          <Stat icon={Users} label="Friends invited" value={data?.count ?? 0} color="text-blue-400" testId="ref-stat-count" />
          <Stat icon={Gift} label="Pro days earned" value={data?.bonus_days_earned ?? 0} color="text-amber-400" testId="ref-stat-days" />
          <Stat icon={Sparkle} label="Your code" value={data?.code || "—"} color="text-emerald-400" testId="ref-stat-code" mono />
        </div>

        <div className="mt-8 bg-[#0F0F12] border border-white/10 rounded-xl p-4 sm:p-6" data-testid="ref-link-card">
          <div className="text-xs tracking-widest uppercase text-zinc-500 mb-2">Your referral link</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={link} readOnly data-testid="ref-link-input"
              className="flex-1 min-w-0 bg-[#050505] border border-white/10 rounded-md px-4 py-3 font-mono text-xs sm:text-sm text-blue-300 outline-none" />
            <button onClick={copyLink} data-testid="ref-copy-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-3 rounded-lg inline-flex items-center justify-center gap-2">
              {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy</>}
            </button>
            <button onClick={shareNative} data-testid="ref-share-btn"
              className="bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium px-5 py-3 rounded-lg">
              Share
            </button>
          </div>
          <p className="text-xs text-zinc-600 mt-3">Tip: post it in a study group, LinkedIn DM, or your bio. The link gives them a personalized welcome page.</p>
        </div>

        <h2 className="text-lg sm:text-xl font-medium mt-10 sm:mt-12 mb-4">Recent invites</h2>
        <div className="bg-[#0F0F12] border border-white/10 rounded-xl overflow-x-auto" data-testid="ref-list">
          {(data?.items || []).length === 0 ? (
            <div className="px-6 py-12 text-center text-zinc-500">No invites yet. Share your link to get started.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-white/5">
                  <th className="px-4 py-3">Friend</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3">Bonus</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.created_at + it.name} className="border-b border-white/5">
                    <td className="px-4 py-3 text-zinc-300">{it.name}</td>
                    <td className="px-4 py-3 text-zinc-500">{new Date(it.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-emerald-400 font-mono">+7 days</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color, testId, mono = false }) => (
  <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
      <Icon size={14} className={color} /> {label}
    </div>
    <div className={`text-3xl font-medium mt-2 ${color} ${mono ? "font-mono" : ""}`}>{value}</div>
  </div>
);
